/* eslint-disable max-len */
import {
    Task as APITask,
    CertificateOfInsuranceNeedsApprovedTaskData,
    CertificateOfInsuranceNeedsSubmittedTaskData,
    ComplianceItemNeedsApprovedTaskData,
    ComplianceItemNeedsResubmittedTaskData,
    RequestStillOpenTaskData,
    SalesNeedApprovedTaskData,
    SubscribeToAlertsTaskData,
    TaskDisplay,
    TaskType,
} from '@centecomm/portal-service';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Hook to map API tasks to TaskDisplay objects.
 * It returns a function that can be used to map tasks, with support for translation.
 * @param currentUserIsOwnerOperator - Whether the current user is an owner or operator.
 * @returns A function that can be used to map tasks.
 */
export const useTaskMapper = (
    currentUserIsOwnerOperator: boolean,
    complianceHubEnabled: boolean = false,
): ((tasks: APITask[]) => TaskDisplay[]) => {
    const { t } = useTranslation(['tasks']);

    return useCallback(
        (tasks: APITask[]) =>
            tasks.map((_) => {
                switch (_.type) {
                    case TaskType.RequestStillOpen: {
                        const taskData = _.data as RequestStillOpenTaskData;

                        return {
                            id: _.id,
                            type: _.type,
                            priority: _.priority,
                            daysOpen: _.daysOpen,
                            title: t('tasks:types.requestNeedsCompleted.title'),
                            subtitle: t('tasks:types.requestNeedsCompleted.subtitle', { requestId: taskData.requestId }),
                            visited: false,
                            link: `/requests/details/${taskData.requestId}`,
                            linkDisplay: t('tasks:types.requestNeedsCompleted.linkDisplay'),
                        };
                    }
                    case TaskType.SalesNeedApproved: {
                        const taskData = _.data as SalesNeedApprovedTaskData;

                        return {
                            id: _.id,
                            type: _.type,
                            priority: _.priority,
                            daysOpen: _.daysOpen,
                            title: t('tasks:types.salesNeedsApproved.title'),
                            subtitle: t('tasks:types.salesNeedsApproved.subtitle', {
                                occupantName: taskData.occupantName,
                                propertyName: taskData.propertyName,
                            }),
                            visited: false,
                            link: complianceHubEnabled
                                ? `/compliance-hub/${taskData.propertyId}/occupants/${taskData.occupantId}/sales`
                                : `/locations/${taskData.propertyId}/occupants/${taskData.occupantId}/sales`,
                            linkDisplay: t('tasks:types.salesNeedsApproved.linkDisplay'),
                        };
                    }
                    case TaskType.SubscribeToAlerts: {
                        const taskData = _.data as SubscribeToAlertsTaskData;

                        return {
                            id: _.id,
                            type: _.type,
                            priority: _.priority,
                            daysOpen: _.daysOpen,
                            title: t('tasks:types.subscribeToAlerts.title'),
                            subtitle: t('tasks:types.subscribeToAlerts.subtitle'),
                            visited: false,
                            link: '/account/general-notifications',
                            linkDisplay: t('tasks:types.subscribeToAlerts.linkDisplay'),
                            mobilePhone: taskData.mobilePhone,
                        } as SubscribeToAlertsTaskData;
                    }
                    case TaskType.PlansNeedsApproved: {
                        const taskData = _.data as ComplianceItemNeedsApprovedTaskData;

                        return {
                            id: _.id,
                            type: _.type,
                            priority: _.priority,
                            daysOpen: _.daysOpen,
                            title: t('tasks:types.genericCompliance.title'),
                            subtitle: t(`tasks:types.genericCompliance.subtitleNeedsReview`, {
                                complianceDisplayName: `"${t(`tasks:types.displayNames.${_.type}`)}"`,
                            }),
                            visited: false,
                            link: complianceHubEnabled
                                ? `/compliance-hub/${taskData.propertyId}/occupants/${taskData.occupantId}/plans`
                                : `/locations/${taskData.propertyId}/occupants/${taskData.occupantId}/compliance`,
                            linkDisplay: t('tasks:types.genericCompliance.linkDisplay'),
                        };
                    }
                    case TaskType.PermitsNeedsApproved: {
                        const taskData = _.data as ComplianceItemNeedsApprovedTaskData;

                        return {
                            id: _.id,
                            type: _.type,
                            priority: _.priority,
                            daysOpen: _.daysOpen,
                            title: t('tasks:types.genericCompliance.title'),
                            subtitle: t(`tasks:types.genericCompliance.subtitleNeedsReview`, {
                                complianceDisplayName: `"${t(`tasks:types.displayNames.${_.type}`)}"`,
                            }),
                            visited: false,
                            link: complianceHubEnabled
                                ? `/compliance-hub/${taskData.propertyId}/occupants/${taskData.occupantId}/permits`
                                : `/locations/${taskData.propertyId}/occupants/${taskData.occupantId}/compliance`,
                            linkDisplay: t('tasks:types.genericCompliance.linkDisplay'),
                        };
                    }
                    case TaskType.SignageNeedsApproved: {
                        const taskData = _.data as ComplianceItemNeedsApprovedTaskData;

                        return {
                            id: _.id,
                            type: _.type,
                            priority: _.priority,
                            daysOpen: _.daysOpen,
                            title: t('tasks:types.genericCompliance.title'),
                            subtitle: t(`tasks:types.genericCompliance.subtitleNeedsReview`, {
                                complianceDisplayName: `"${t(`tasks:types.displayNames.${_.type}`)}"`,
                            }),
                            visited: false,
                            link: complianceHubEnabled
                                ? `/compliance-hub/${taskData.propertyId}/occupants/${taskData.occupantId}/signage`
                                : `/locations/${taskData.propertyId}/occupants/${taskData.occupantId}/compliance`,
                            linkDisplay: t('tasks:types.genericCompliance.linkDisplay'),
                        };
                    }
                    case TaskType.CompleteLeasingApplication: {
                        return {
                            id: _.id,
                            type: _.type,
                            priority: _.priority,
                            daysOpen: _.daysOpen,
                            title: t('tasks:types.completeLeasingApplication.title'),
                            visited: false,
                            link: `/app/lease-application`,
                            linkDisplay: t('tasks:types.completeLeasingApplication.linkDisplay'),
                        };
                    }
                    case TaskType.CertificateOfInsuranceNeedsApproved: {
                        const taskData = _.data as CertificateOfInsuranceNeedsApprovedTaskData;

                        return {
                            id: _.id,
                            type: _.type,
                            priority: _.priority,
                            daysOpen: _.daysOpen,
                            title: t('tasks:types.coiNeedsApproved.title'),
                            subtitle: t('tasks:types.coiNeedsApproved.subtitle', { occupantName: taskData.occupantName }),
                            visited: false,
                            link: `/insurance`,
                            linkDisplay: t('tasks:types.coiNeedsApproved.linkDisplay'),
                        };
                    }
                    case TaskType.HvacNeedsApproved: {
                        const taskData = _.data as ComplianceItemNeedsApprovedTaskData;
                        return {
                            id: _.id,
                            type: _.type,
                            priority: _.priority,
                            daysOpen: _.daysOpen,
                            title: t('tasks:types.hvacNeedsApproved.title'),
                            subtitle: t('tasks:types.hvacNeedsApproved.subtitle', { occupantName: taskData.occupantName }),
                            visited: false,
                            link: complianceHubEnabled
                                ? `/compliance-hub/${taskData.propertyId}/occupants/${taskData.occupantId}/hvac`
                                : `/locations/${taskData.propertyId}/occupants/${taskData.occupantId}/hvac`,
                            linkDisplay: t('tasks:types.hvacNeedsApproved.linkDisplay'),
                        };
                    }
                    case TaskType.CertificateOfInsuranceNeedsSubmitted: {
                        const taskData = _.data as CertificateOfInsuranceNeedsSubmittedTaskData;

                        return {
                            id: _.id,
                            type: _.type,
                            priority: _.priority,
                            daysOpen: _.daysOpen,
                            title: t('tasks:types.coiNeedsSubmitted.title'),
                            subtitle: t('tasks:types.coiNeedsSubmitted.subtitle', { occupantName: taskData.occupantName }),
                            visited: false,
                            link: currentUserIsOwnerOperator
                                ? complianceHubEnabled
                                    ? `/compliance-hub/${taskData.propertyId}/occupants/${taskData.occupantId}/insurance`
                                    : `/locations/${taskData.propertyId}/occupants/${taskData.occupantId}/insurance`
                                : `/business/${taskData.occupantId}/insurance`,
                            linkDisplay: t('tasks:types.coiNeedsSubmitted.linkDisplay'),
                        };
                    }
                    case TaskType.SignageNeedsResubmitted: {
                        return {
                            id: _.id,
                            type: _.type,
                            priority: _.priority,
                            daysOpen: _.daysOpen,
                            title: t('tasks:types.signageNeedsResubmitted.title'),
                            visited: false,
                            link: `/business/${(_.data as ComplianceItemNeedsResubmittedTaskData).occupantId}/compliance`,
                            linkDisplay: t('tasks:types.signageNeedsResubmitted.linkDisplay'),
                        };
                    }
                    case TaskType.PlansNeedsResubmitted: {
                        return {
                            id: _.id,
                            type: _.type,
                            priority: _.priority,
                            daysOpen: _.daysOpen,
                            title: t('tasks:types.plansNeedsResubmitted.title'),
                            visited: false,
                            link: `/business/${(_.data as ComplianceItemNeedsResubmittedTaskData).occupantId}/compliance`,
                            linkDisplay: t('tasks:types.plansNeedsResubmitted.linkDisplay'),
                        };
                    }
                    case TaskType.PermitsNeedsResubmitted: {
                        return {
                            id: _.id,
                            type: _.type,
                            priority: _.priority,
                            daysOpen: _.daysOpen,
                            title: t('tasks:types.permitsNeedsResubmitted.title'),
                            visited: false,
                            link: `/business/${(_.data as ComplianceItemNeedsResubmittedTaskData).occupantId}/compliance`,
                            linkDisplay: t('tasks:types.permitsNeedsResubmitted.linkDisplay'),
                        };
                    }
                    default:
                        // Internal error, no need to translate
                        throw new Error('Unsupported task type');
                }
            }),
        [currentUserIsOwnerOperator, t, complianceHubEnabled],
    );
};

export default useTaskMapper;
